import {
  castSkill,
  openStation,
  returnToHub,
  teleportToStation,
  usePotion,
  useGate,
} from '../net/protocol';
import type { GameStore } from '../state/store';
import { slotForKey } from '../ui/actionbar';
import type { Navigator } from './navigate';
import { GATE_ARRIVAL_MARGIN } from './input';

/**
 * Which key opens what. Compared case-insensitively against `KeyboardEvent.key`,
 * so it works on any layout that produces the letter.
 */
export const KEYBINDS = {
  /** The bag: the paper doll and what the player is carrying. */
  stash: 'i',
  character: 'c',
  skills: 'k',
  passives: 'p',
  /**
   * The social panel: who is online, and who to invite.
   *
   * `O` for online — `S` is the workshops, `P` is the passives, and `F` is the vacuum. It
   * opens unconditionally, so unlike the workshops it belongs in `PANELS` and gets its
   * button in the rail for free.
   */
  social: 'o',
  system: 'Escape',
  /**
   * The potion belt.
   *
   * Not a panel — it is an action, and it is the one key here that does something in
   * the world rather than opening something over it. `Q` because 1-4 are the belt's
   * keys in the written design and the action bar already owns 1-5; see
   * internal/game/potion.go for why that conflict is resolved by scope.
   */
  potion: 'q',
  /**
   * The town portal, from HUB-ZONES-SCRAP-UI.md's global hotkeys.
   *
   * The spec gives it a 3-second cast that combat damage interrupts. There is no cast
   * system and no interrupt, so this is instant — and it is not a new power either
   * way: `CMD_RETURN_TO_HUB` has always been available from the death screen and the
   * on-screen button, from anywhere, including while dead. This is the keybind it
   * never had.
   */
  town: 't',
  /**
   * The workshops, from the spec's global hotkeys.
   *
   * Town only, because they are. It opens the **first** workshop rather than a chooser:
   * every workshop does the same job today, so a menu would be a step between the key and
   * the thing it opens.
   *
   * It was `scrap`, when bulk-scrapping was what a workshop was for. Scrapping is gone
   * until crafting lands — it paid a placeholder currency for what will become real
   * materials — and the counter behind this key sells instead.
   */
  workshops: 's',
  /**
   * The stash — the town's storage fixture, not the bag.
   *
   * `v` for vault, which is what the fixture was called before it was split off from the
   * Arcane Vault and what players still call the place they leave things. It could not be
   * `s` (the workshops) and it could not be `stash` in this table either: **`KEYBINDS.stash`
   * is already the bag**, a misnomer kept on purpose because renaming a `MenuPanel` member
   * costs a rename across the router, the HUD bar, four components and their specs for no
   * behaviour. So the fixture gets its own name here rather than fighting over that one.
   *
   * Town only, and routed by hand rather than through `PANELS`, for the reason the
   * workshops are: it needs a fixture the server actually sent, and a key advertised in a
   * dungeon that says *not in town* is worse than no key. Its affordance is the town bar,
   * which appears only in town, which is where it works.
   */
  storage: 'v',
  /**
   * The run: opens the level select, the same panel the [ GRIND ] gate and the button do.
   *
   * `g` for grind, and it was free. Town only, because the gate is — a key that offered a
   * dive from inside a dungeon would be a panel over a fight, and the thing it opens
   * would have nothing to choose between.
   */
  grind: 'g',
  /**
   * The dodge roll.
   *
   * Spacebar, per `doc/grind/design/DODGE.md`, and it was genuinely unbound —
   * the one prominent key in the game that did nothing. Like the belt it is an action
   * in the world rather than a panel, so it is refused while something covers the
   * canvas.
   *
   * `' '` is what `KeyboardEvent.key` reports for the spacebar. Not `'Space'`, which is
   * `event.code`; matching on the wrong one silently binds nothing.
   */
  dodge: ' ',
  /**
   * The loot-filter editor, from `doc/grind/design/LOOT-FILTER.md`.
   *
   * `L` for loot. It is an ordinary panel — it opens anywhere, in town or in a zone,
   * alive or dead — so unlike the workshops it belongs in `PANELS` and gets its button
   * in the HUD bar for free.
   */
  filter: 'l',
  /**
   * The vacuum, from the spec's layout-engine section: "Pressing `F` automatically picks
   * up all visible Tier 1 and Tier 2 filter items within a 5-meter radius".
   *
   * An action in the world, so it is refused behind a panel exactly as the belt and the
   * action bar are — and it sends **the same `CMD_PICKUP` a click sends**, one per drop,
   * throttled. A filter is presentation only: this is a faster way to *ask*, never a way
   * to take something the server would refuse a click for.
   */
  vacuum: 'f',
  /**
   * The developer panel, from Phase 1 of the roadmap.
   *
   * `F9` because it is unmistakably a debug key and collides with nothing: `F1`–`F5`
   * are the workshops, `F11` and `F12` are the browser's and cannot be taken, and the
   * letter keys are all either panels or world actions. It is compared exactly, like
   * the station keys, because `KeyboardEvent.key` reports `"F9"`.
   *
   * **Conditional**, so it is routed by hand and not through `PANELS`: it does nothing
   * unless the server said test mode is on. A key advertised in the HUD bar that did
   * nothing in most sessions would be worse than no key — the same reasoning that keeps
   * `S` out of that list.
   */
  test: 'F9',
} as const;

/**
 * A function key per workshop, in the order the town lays them out.
 *
 * The F row is the natural home for these: the game uses none of it, and unlike the
 * number row (which the action bar owns 1-5) there is nothing to collide with. `F1`
 * is the Forge, `F2` the Tannery, and so on — the same order as the icon row, which
 * prints the key on each icon so the shortcut shown is the shortcut that works.
 *
 * ── What the browser keeps ──────────────────────────────────────────────────
 *
 * These are *browser* keys before they are ours. `preventDefault()` on `keydown`
 * suppresses the default for most of the row, and `attachKeyboard` already calls it
 * for every key it handles. But two are not ours to take at all: **`F11`
 * (fullscreen) and `F12` (devtools) cannot be prevented**, which is why the range
 * stops well short of them.
 *
 * **`F5` is the one to know about.** It is reload, and while `preventDefault` does
 * stop it in current Chrome and Firefox, that is a browser behaviour rather than a
 * guarantee — and this game has **no persistence**, so a reload that gets through
 * costs the character outright. It is bound here because five workshops want five
 * keys and asymmetry is worse than a documented risk, but it is one line to move: the
 * list below is the only place a workshop key is named.
 */
export const STATION_KEYS = ['F1', 'F2', 'F3', 'F4', 'F5'] as const;

/**
 * The panels a player can open, with the key that opens each.
 *
 * One list, read by both the keyboard router and the HUD's menu bar, so a
 * shortcut shown on screen is the shortcut that actually works. Adding a panel
 * in one place and forgetting the other is the failure this prevents.
 *
 * **`station` is deliberately not here**, even though it is a panel with a key. Every
 * entry in this list must open unconditionally — that is the invariant the walking test
 * checks — and the workshops are town-only and need a station the server actually sent.
 * Advertising `S` in a bar that is visible in a dungeon would be advertising a key that
 * does nothing there. Its affordance is `StationBar`, which appears only in town, which
 * is where it works.
 *
 * **`test` is out for the same reason**, and more so: the developer panel exists only
 * when the active league permits a developer command, which is a server fact and false
 * in every official league. Its affordance is `TestModeBar`.
 */
export const PANELS = [
  { panel: 'character', label: 'Character', key: 'C', sigil: '◈' },
  // `stash` is the panel value's name and `Bag` is what it now shows: the doll plus the
  // carried grid. The value is deliberately unchanged — renaming a `MenuPanel` member is
  // a rename across the keyboard router, the HUD bar, four components and their specs,
  // for no behaviour. The **vault** is not in this list at all: it is a place in town,
  // reached at the Arcane Vault, and a key that said "not in town" for most of a session
  // would be worse than no key. See `ui/VaultSection.tsx`.
  { panel: 'stash', label: 'Bag', key: 'I', sigil: '▤' },
  { panel: 'skills', label: 'Skills', key: 'K', sigil: '✦' },
  { panel: 'passives', label: 'Passives', key: 'P', sigil: '❖' },
  { panel: 'social', label: 'Social', key: 'O', sigil: '☍' },
  { panel: 'filter', label: 'Filter', key: 'L', sigil: '⌗' },
  { panel: 'system', label: 'Menu', key: 'ESC', sigil: '≡' },
] as const;

/**
 * Routes a keypress to the store. Exported separately from the listener so it
 * can be tested without touching the document.
 *
 * Returns true when the key was handled, which tells the caller to swallow the
 * event — otherwise `I` would also reach the browser's own shortcuts.
 */
export function handleKey(store: GameStore, key: string, navigator?: Navigator): boolean {
  if (store.phase() !== 'playing') return false;

  // Escape is a back button before it is a menu key: it closes whatever is up,
  // and only opens the system menu from a clear screen. Otherwise leaving the
  // vault would drop you straight into settings.
  if (key === KEYBINDS.system) {
    if (store.menuOpen()) {
      store.closeMenu();
    } else {
      store.openMenu('system');
    }
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.stash) {
    store.toggleMenu('stash');
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.character) {
    store.toggleMenu('character');
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.skills) {
    store.toggleMenu('skills');
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.passives) {
    store.toggleMenu('passives');
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.social) {
    store.toggleMenu('social');
    return true;
  }

  if (key.toLowerCase() === KEYBINDS.filter) {
    store.toggleMenu('filter');
    return true;
  }

  // The vacuum. An action in the world, so it is refused while a panel covers the
  // canvas — and it has to be, or `F` typed over the filter editor would be sweeping
  // the floor behind it. (Keystrokes inside the editor's textarea never reach here at
  // all; `attachKeyboard` leaves text entry alone. This is the case where the panel is
  // open but the focus is not in it.)
  //
  // Routed straight to the store rather than through the Navigator: a pickup is not
  // movement, the player does not walk to it, and the server refuses anything out of
  // its own range. Nothing to predict.
  if (!store.menuOpen() && key.toLowerCase() === KEYBINDS.vacuum) {
    store.vacuumLoot();
    return true;
  }

  // The roll. An action in the world, so it is refused behind a panel, exactly like the
  // belt and the action bar.
  //
  // It goes through the **Navigator** rather than straight to `store.send`, because a
  // roll is movement: predicting it and sending it have to happen together and in that
  // order, and the route has to be dropped. That is the one thing this file cannot do on
  // its own, which is why the navigator is threaded in — `attachKeyboard` supplies it.
  //
  // Aimed at the cursor, like a skill: rolling *where you are looking* is what makes it
  // an escape rather than a lunge. With no cursor yet the server falls back to the
  // player's facing, and passing `null` here asks it to.
  if (!store.menuOpen() && key === KEYBINDS.dodge) {
    // Without a navigator there is nothing that can both predict and send, and sending
    // alone would be a 4-unit snap on every press. Swallowed regardless so the key
    // never reaches the browser and scrolls the page.
    const predicted = navigator?.roll(store.cursorWorld() ?? null, store.self()?.facing ?? 0);

    // **Answer immediately when the client already knows.** The server will refuse this
    // press too and send the same wording, but that is a round trip away and the roll it
    // refers to is only 300 ms long — waiting for it is what makes a key feel broken.
    // Which reason is chosen matters: "out of stamina" and "still on cooldown" are
    // different problems with different fixes, and conflating them is exactly the
    // confusion the indicator exists to remove.
    if (predicted === false) {
      const state = store.dodgeState();
      store.refuseDodgeLocally(state.staminaShort ? 'RESOURCES' : 'COOLDOWN');
    }
    return true;
  }

  // Drinking is an action in the world, so it is refused while a panel covers the
  // canvas — the same rule the action bar's number keys follow. The server decides
  // whether anything happens; this only sends the intent.
  if (!store.menuOpen() && key.toLowerCase() === KEYBINDS.potion) {
    store.send(usePotion());
    return true;
  }

  // The town portal. Allowed with a panel open, unlike the world actions: it is a
  // *navigation* key, and having to close the vault before you can go home is exactly
  // the friction the hub spec exists to remove. The server refuses it in town.
  //
  // It **opens** a portal rather than travelling — the gate appears a few units away
  // and walking into it is what goes home. So this key is not an escape from a fight,
  // and it deliberately does not go through `Navigator`: nothing about it is predicted,
  // because the server chose where the portal stands and the walk to it is an ordinary
  // walk the player steers themselves.
  if (key.toLowerCase() === KEYBINDS.town) {
    // **In town it is the way *back*.** The same key, the other half of the round trip:
    // in a zone it opens a gate home, and at home it takes you through the gate standing
    // in town. Sending `CMD_RETURN_TO_HUB` here did nothing at all — the server refuses
    // it in town — so the key was dead in exactly the place a player most wants it.
    //
    // It walks first, because a gate has a range the server measures. That is what
    // clicking the gate does, so the key and the click are one behaviour rather than two.
    if (store.inHub()) {
      const gate = store.portals()[0];
      if (!gate || !navigator) return false;
      navigator.goTo(gate.x, gate.z, {
        at: { x: gate.x, z: gate.z },
        radius: Math.max(1, store.portalEntryRange() - GATE_ARRIVAL_MARGIN),
        done: () => store.send(useGate('PORTAL')),
      });
      return true;
    }

    store.send(returnToHub());
    return true;
  }

  // The developer panel. Routed by hand rather than through `PANELS`, because it opens
  // only when the **server** said test mode is on — and returning false when it did not
  // means the key reaches the browser untouched, which is the honest behaviour for a key
  // this session does not have.
  //
  // Allowed with a panel open, like the town portal: it is not a world action, and
  // `toggleMenu` closes whatever was up.
  if (key === KEYBINDS.test) {
    if (!store.testMode()) return false;
    store.toggleMenu('test');
    return true;
  }

  // A function key per workshop. Compared exactly rather than lowercased: `KeyboardEvent.key`
  // is `"F1"`, and lowercasing it would collide with nothing but would read as though
  // `f1` were a thing.
  //
  // Indexed against the stations **the server actually sent**, so a key for a workshop
  // that does not exist does nothing rather than teleporting to the wrong one.
  const stationKey = STATION_KEYS.indexOf(key as (typeof STATION_KEYS)[number]);
  if (stationKey >= 0) {
    const station = store.stations()[stationKey];
    if (!station) return false;
    store.send(teleportToStation(station.kind));
    // **And ask to open it.** `CMD_TELEPORT_TO_STATION` moves the player and nothing else; the
    // server's `p.Station` — the only thing that decides which shelf `toWireVendor` sends — is set
    // by `CMD_OPEN_STATION` alone. Without this the key stood you at the counter, opened the panel
    // locally, and left the server believing no shop was open, so the vendor payload arrived as
    // `{ rerollInSec: 0, offers: [] }` for a shelf that was fully stocked. Reported as *there is
    // still no items to buy in shop ever*; the click and walk-up paths always sent both, which is
    // what made it look like a shop bug rather than a keybind one.
    store.send(openStation(station.kind));
    store.openStation(station.kind);
    return true;
  }

  // The workshops. Town only, and a key that opened a shop in a dungeon
  // would be a panel over a fight. Teleports to the station as well as opening it, so
  // the key and the icon do exactly the same thing.
  if (key.toLowerCase() === KEYBINDS.workshops) {
    const first = store.stations()[0];
    if (!first) return false;
    store.send(teleportToStation(first.kind));
    // Both commands, for the reason above: the icon and the key must do exactly the same thing,
    // and the icon has always sent both.
    store.send(openStation(first.kind));
    store.openStation(first.kind);
    return true;
  }

  // The stash. The same three steps a click on its button performs — stand there, ask to
  // open it, show the panel — because both routes must go through the server's one
  // authority on whether a fixture may be open.
  if (key.toLowerCase() === KEYBINDS.storage) {
    if (!store.inHub()) return false;
    const stash = store.stations().find((s) => s.kind === 'STASH');
    if (!stash) return false;
    store.send(teleportToStation(stash.kind));
    store.send(openStation(stash.kind));
    store.openStation(stash.kind);
    return true;
  }

  // The run. Town only, for the reason the workshops are: the level select has nothing
  // to offer from inside a zone, and the gate it mirrors is not there either.
  if (key.toLowerCase() === KEYBINDS.grind) {
    if (!store.inHub()) return false;
    store.openMenu('portal');
    return true;
  }

  // The number keys fire the action bar. Only while nothing is open: a number typed with the
  // vault up should not also throw a skill across the room.
  //
  // **Aimed at the cursor.** Most of the catalogue used to need a monster clicked
  // first — 69 of 100 skills — so a key on its own did nothing. Passing the world
  // point under the pointer lets the server hit whatever is standing there, and a
  // cast at empty ground still goes off.
  //
  // The slot comes from `slotForKey` rather than `Number(key) - 1`: the bar holds six
  // slots and only five of them have a key, so the arithmetic would fire the mouse's slot
  // for a `6` nobody bound — and it would go on looking right while doing it.
  const slot = store.menuOpen() ? null : slotForKey(key);
  if (slot !== null) {
    store.send(castSkill(slot, store.cursorWorld() ?? undefined));
    return true;
  }

  return false;
}

/**
 * Binds the game's keyboard shortcuts to the document, and returns a detach
 * function.
 *
 * Keystrokes inside a text field are left alone — typing "i" into the vault's
 * search box must not close the vault.
 */
export function attachKeyboard(
  store: GameStore,
  target: Document = document,
  navigator?: Navigator,
): () => void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    if (isTypingTarget(event.target)) return;

    if (handleKey(store, event.key, navigator)) event.preventDefault();
  };

  /**
   * ── Hold Alt to reveal the floor ──────────────────────────────────────────────────────
   *
   * Its own pair of listeners rather than a line in `handleKey`, for three reasons that all
   * come from Alt being a *modifier*:
   *
   * - It is **held**, not pressed. Every other binding here fires once on `keydown`; this one
   *   needs the release too, and `handleKey` has no `keyup` path.
   * - `onKeyDown` above returns early on `event.altKey`, which is correct — `Alt+1` must not
   *   cast — and would swallow this before it started.
   * - **The browser steals it.** Alt alone focuses the menu bar on Windows and Linux, which
   *   takes keyboard focus off the page mid-fight; `preventDefault` is what stops that, and it
   *   has to happen on the modifier's own event.
   *
   * `blur` matters as much as `keyup`: alt-tabbing away sends the down and never the up, so
   * without it the floor would still be revealed on return with nothing holding it.
   */
  const onRevealDown = (event: KeyboardEvent) => {
    if (event.key !== 'Alt' || isTypingTarget(event.target)) return;
    event.preventDefault();
    store.setLootRevealed(true);
  };
  const onRevealUp = (event: KeyboardEvent) => {
    if (event.key !== 'Alt') return;
    store.setLootRevealed(false);
  };
  const onBlur = () => store.setLootRevealed(false);

  target.addEventListener('keydown', onKeyDown);
  target.addEventListener('keydown', onRevealDown);
  target.addEventListener('keyup', onRevealUp);
  const view = (target as Document).defaultView;
  view?.addEventListener('blur', onBlur);

  return () => {
    target.removeEventListener('keydown', onKeyDown);
    target.removeEventListener('keydown', onRevealDown);
    target.removeEventListener('keyup', onRevealUp);
    view?.removeEventListener('blur', onBlur);
  };
}

/** True when the event came from somewhere the player is entering text. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}
